/*
 * Copyright (c) 2022 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { appConstants } from '../data-model/app-constants';
import { Subject, Observable, BehaviorSubject, Subscription } from 'rxjs';
import { Logger } from '@core/lib/services';
import { BaseService } from './base.service';
import { LookupData } from '../data-model/lookup.types';
import { TranslateService } from '@ngx-translate/core';
import { ProxyRequest } from '../data-model/proxy.types';

const log = new Logger('LookupService');

@Injectable({
  providedIn: 'root'
})
export class LookupService implements OnDestroy {
  // Local Property
  lookUpData: LookupData = {};
  private serviceDetailsSubscription: Subscription;

  // Observer
  lookUpDataObserver: Subject<LookupData> = new BehaviorSubject(this.lookUpData);
  getLookUpData(): Observable<LookupData> {
    return this.lookUpDataObserver.asObservable();
  }
  setLookUpData(data: LookupData) {
    this.lookUpDataObserver.next(data);
  }

  // Constructor
  constructor(private baseService: BaseService, private translate: TranslateService) {
    this.lookUpData.gender = [
      { code: 'M', desc: this.translate.instant('label.gender.male') },
      { code: 'F', desc: this.translate.instant('label.gender.female') },
      { code: 'O', desc: this.translate.instant('label.other') },
      { code: ' ', desc: this.translate.instant('label.unknown') }
    ];
    this.lookUpData.confidentiality = [
      { code: 'No', desc: this.translate.instant('label.no') },
      { code: 'Yes', desc: this.translate.instant('label.yes') }
    ];
  }

  // Service initialize / reinitialize
  initialize(): void {
    // Get Service Map and get all lookup data
    this.lookUpData.error = false;
    this.serviceDetailsSubscription = this.baseService.getServiceDetails().subscribe(details => {
      this.fetchLookup(details?.lookUp);
    });
  }

  ngOnDestroy(): void {
    this.serviceDetailsSubscription && this.serviceDetailsSubscription.unsubscribe();
  }

  // Base function to load the lookup
  private fetchLookup(lookUpDetails: Record<string, ProxyRequest>) {
    if (lookUpDetails && Object.keys(lookUpDetails).length) {
      // Utilizing priorities call as health check, replace with health api once available
      this.fetchData(lookUpDetails, appConstants.lookup.PRIORITIES)
        .then(() => {
          log.debug('Record-Management LookupService API healthy', this.lookUpData);
          Promise.all([
            this.fetchData(lookUpDetails, appConstants.lookup.PATIENT_LOCATIONS),
            this.fetchData(lookUpDetails, appConstants.lookup.AUTHORITIES),
            this.fetchData(lookUpDetails, appConstants.lookup.PHYSICIANS),
            this.fetchData(lookUpDetails, appConstants.lookup.PROCEDURES),
            this.fetchData(lookUpDetails, appConstants.lookup.DEPARTMENTS),
            this.fetchData(lookUpDetails, appConstants.lookup.ORDERING_LOCATIONS)
          ]).finally(() => {
            this.processPriorityDisabled();
            this.setLookUpData(this.lookUpData);
            log.debug('Record-Management LookupService lookup updated', this.lookUpData);
          });
        })
        .catch(() => {
          this.setLookUpData(this.lookUpData);
          log.error('Record-Management LookupService API unhealthy');
        });
    }
  }

  /**
   * Helper functions
   */
  // Function to call proxy
  private fetchData(lookUpDetails: Record<string, ProxyRequest>, dataMap: string): Promise<void> {
    const payload = lookUpDetails[dataMap];
    return new Promise<void>((resolve, reject) => {
      if (payload && payload.configValue) {
        const promise = this.baseService.loadData(appConstants.appURLs.proxyFetch, payload);
        promise.then(data => {
          this.lookUpData[dataMap] = data;
          resolve();
        });
        promise.catch(() => {
          log.error('Exception in lookup fetch for ' + dataMap);
          this.lookUpData.error = true;
          reject();
        });
      } else {
        log.error('Missing details in service for ' + dataMap);
        this.lookUpData.error = true;
        reject();
      }
    });
  }

  // Process priority field for disabled entries
  private processPriorityDisabled(): void {
    if (this.lookUpData.fetchPriorities) {
      Object.entries(this.lookUpData.fetchPriorities).forEach(
        ([_key, value]) => (value.disabled = (value.priorityFlags & (1 << 0)) === 0)
      );
    }
  }

  // Search by ID
  findGenderById(id: string) {
    const gender = ['M', 'F', 'O'].includes(id) ? id : ' ';
    return this.lookUpData.gender.find(item => item.code === gender);
  }
  findConfidentialityById(id: string) {
    return this.lookUpData.confidentiality.find(item => item.code.toLowerCase() === id.toLowerCase());
  }
  findpatientLocationsById(id: number | string) {
    return this.lookUpData.fetchPatientLocations.find(item => item.locationId === id);
  }
  findpatientLocationsByName(loc: string) {
    return this.lookUpData.fetchPatientLocations.find(item => item.locationDesc.toLowerCase() === loc.toLowerCase());
  }
  findAuthoritiesById(id: number | string) {
    return this.lookUpData.fetchAuthorities.find(item => item.internalAssigningAuthorityId === id);
  }
  findPrioritiesById(id: number | string) {
    return this.lookUpData.fetchPriorities.find(item => item.internalPriorityId === id);
  }
  findPhysiciansById(id: number | string) {
    return this.lookUpData.fetchPhysicians.find(item => item.internalStid === id);
  }
  findProceduresById(id: number | string) {
    return this.lookUpData.fetchProcedures.find(item => item.internalProcedureId === id);
  }
  findDepartmentById(id: number | string) {
    return this.lookUpData.fetchDepartment.find(item => item.internalDepartmentId === id);
  }
  findOrderingLocationsById(id: number | string) {
    return this.lookUpData.fetchOrderingLocations.find(item => item.orderingLocationId === id);
  }
}
